import app from 'containers/app'
import { createAction } from 'redux-actions'
import { actions as OrgActions } from 'app/modules/org'
import { actions as UserActions, selectors as UserSelectors } from 'app/modules/user'
import { actions as TrackingActions } from 'app/modules/tracking'
import { onOnboarding } from 'app/modules/router/selectors'
import { captureException } from 'utils/SentryHelpers'
import { patchOperations } from 'utils/PatchHelper'
import * as FileAPI from 'app/api/FileAPI'
import { api } from './api'
import { constants } from './constants'
import * as selectors from './selectors'
import * as selectorsUI from './selectorsUI'

export const setProfileUI = createAction(constants.SET_PROFILE_UI)
export const setProfileUIData = createAction(constants.SET_PROFILE_UI_DATA)

export const fetchCurrentProfileStarted = createAction(constants.FETCH_CURRENT_PROFILE_STARTED)
export const fetchCurrentProfileFailed = createAction(constants.FETCH_CURRENT_PROFILE_FAILED)

export const updateProfileStarted = createAction(constants.UPDATE_PROFILE_STARTED)
export const updateProfileFailed = createAction(constants.UPDATE_PROFILE_FAILED)
export const updateProfileSuccess = createAction(constants.UPDATE_PROFILE_SUCCESS)

export const checkUserHandleExistenceStarted = createAction(constants.CHECK_USER_HANDLE_EXISTENCE_STARTED)
export const checkUserHandleExistenceFailed = createAction(constants.CHECK_USER_HANDLE_EXISTENCE_FAILED)
export const receiveUserHandleExistence = createAction(constants.RECEIVE_USER_HANDLE_EXISTENCE)
export const receiveUserGlobalHandleExistence = createAction(constants.RECEIVE_USER_GLOBAL_HANDLE_EXISTENCE)

export const setVisibilityStarted = createAction(constants.SET_VISIBILITY_STARTED)
export const setVisibilityFailed = createAction(constants.SET_VISIBILITY_FAILED)

export const receiveProfile = createAction(constants.RECEIVE_PROFILE)

export const fetchUserProfile = createAction(constants.FETCH_USER_PROFILE)
export const fetchUserProfilePending = createAction(constants.FETCH_USER_PROFILE_PENDING)
export const fetchUserProfileSuccess = createAction(constants.FETCH_USER_PROFILE_SUCCESS)
export const fetchUserProfileFailed = createAction(constants.FETCH_USER_PROFILE_FAILED)

export const fetchCurrentProfile = () => (dispatch, getState) => {
  return dispatch(UserActions.fetchCurrentUser()).then(() => {
    const state = getState()
    if (shouldFetchProfile(state)) {
      const user = UserSelectors.currentUser(state)

      dispatch(fetchCurrentProfileStarted())
      return Promise.all([_fetchCurrentProfile(user.id, dispatch), dispatch(OrgActions.fetchOrg())])
    }
  })
}

function _fetchCurrentProfile(userId, dispatch) {
  return api
    .fetch(userId)
    .then(data => dispatch(receiveProfile(data)))
    .catch(error => {
      captureException(error)
      dispatch(fetchCurrentProfileFailed())
      throw error
    })
}

function shouldFetchProfile(state) {
  return selectors.profileFetchStatus(state) !== constants.RECEIVE_PROFILE
}

const updateSectionFactory = (uiSelector, selector) => () => (dispatch, getState) => {
  return dispatch(UserActions.fetchCurrentUser()).then(() => {
    const state = getState()
    const user = UserSelectors.currentUser(state)
    const oldProfile = selector(state).toJS()
    const newProfile = uiSelector(state).toJS()
    return _updateProfile(user.id, newProfile, oldProfile, dispatch, state)
  })
}

export const updateProfile = updateSectionFactory(selectorsUI.profile, selectors.profile)
export const updateOverview = updateSectionFactory(selectorsUI.overview, selectors.overview)
export const updateContact = updateSectionFactory(selectorsUI.social, selectors.social)
export const updateDetails = updateSectionFactory(selectorsUI.details, selectors.details)
export const updateMessaging = updateSectionFactory(selectorsUI.messaging, selectors.messaging)
export const updateTheme = updateSectionFactory(selectorsUI.theme, selectors.theme)

export const createQuickProfile = () => (dispatch, getState) => {
  return dispatch(UserActions.fetchCurrentUser()).then(() => {
    const state = getState()
    const user = UserSelectors.currentUser(state)

    // should be action --> reducer --> thunk
    const operations = [
      {
        operation: 'set',
        property: 'layout',
        value: 'WIDE',
      },
      {
        operation: 'set',
        property: 'visibility',
        value: 'PUBLIC',
      },
    ]

    api
      .update(user.id, operations)
      .then(data => {
        dispatch(updateProfileSuccess(data))
        dispatch(receiveProfile(data))
      })
      .catch(error => {
        captureException(error)
        dispatch(updateProfileFailed())
        throw error
      })
  })
}

function _updateProfile(userId, profileUI, profile, dispatch, state) {
  const operations = patchOperations(profileUI, profile)
  dispatch(updateProfileStarted())
  if (operations.length > 0) {
    api
      .update(userId, operations)
      .then(data => {
        dispatch(updateProfileSuccess(data))
        dispatch(receiveProfile(data))
        if (!onOnboarding(state)) {
          app.actions.NotificationActions.pushNotification({
            content: 'Your Drift Profile has been saved!',
            variant: 'green',
          })
        }
        if (!profile.handle && profileUI.handle) {
          dispatch(
            TrackingActions.track('Created Profile', {
              context: 'existing-user',
              handle: profileUI.handle,
            })
          )
        } else {
          dispatch(TrackingActions.track('Updated Profile', { operations }))
        }
      })
      .catch(error => {
        app.actions.NotificationActions.pushNotification({
          content: 'An error occurred while updating your profile settings. Please try again later.',
          variant: 'red',
        })
        captureException(error)
        dispatch(updateProfileFailed())
        throw error
      })
  }
}

export const checkGlobalUserHandle = globalHandle => (dispatch, getState) => {
  const handle = globalHandle || selectorsUI.handle(getState())
  if (!handle.length > 0) return
  dispatch(checkUserHandleExistenceStarted())
  return api.checkGlobalHandle(handle).then(res => {
    if (res.status === 404) {
      return dispatch(receiveUserGlobalHandleExistence({ globalHandle, valid: true }))
    } else if (res.status === 200) {
      return dispatch(receiveUserGlobalHandleExistence({ valid: false }))
    } else {
      captureException(new Error(`Unable to check handle existence for global handle ${handle}`))
    }
  })
}

export const checkUserHandle = handle => (dispatch, getState) => {
  const newHandle = handle || selectorsUI.handle(getState())
  if (!newHandle.length > 0) return
  return api.checkUserHandle(newHandle).then(res => {
    if (res.status === 404) {
      return dispatch(receiveUserHandleExistence({ valid: true }))
    } else if (res.status === 200) {
      return dispatch(receiveUserHandleExistence({ valid: false }))
    } else {
      captureException(new Error(`Unable to check org handle existence for handle ${newHandle}`))
    }
  })
}

export const setVisibility = (visible, notify = true) => (dispatch, getState) => {
  const state = getState()
  const user = UserSelectors.currentUser(state)
  const originalVisibility = selectors.visibility(state)

  dispatch(setVisibilityStarted(visible))
  return api
    .update(user.id, getOperations(visible))
    .then(() => {
      if (notify) {
        app.actions.NotificationActions.pushNotification({
          content: 'Your profile’s privacy has been saved!',
          variant: 'green',
        })
      }
    })
    .catch(error => {
      app.actions.NotificationActions.pushNotification({
        content: 'An error occurred while updating your profile visibility. Please try again later.',
        variant: 'red',
      })
      dispatch(setVisibilityFailed(originalVisibility))
      captureException(error)
      throw error
    })
}

function getOperations(visible) {
  return [
    {
      operation: 'set',
      property: 'visibility',
      value: visible,
    },
  ]
}

export const clearCoverPhoto = createAction(constants.CLEAR_COVER_PHOTO)
export const uploadCoverPhotoStarted = createAction(constants.UPLOAD_COVER_PHOTO_STARTED)
export const uploadCoverPhotoSuccess = createAction(constants.UPLOAD_COVER_PHOTO_SUCCESS)
export const uploadCoverPhotoFailed = createAction(constants.UPLOAD_COVER_PHOTO_FAILED)
export const uploadCoverPhoto = file => dispatch => {
  dispatch(uploadCoverPhotoStarted({ file }))
  FileAPI.upload(file)
    .then(response => {
      dispatch(uploadCoverPhotoSuccess(response))
      dispatch(updateProfile())
    })
    .catch(error => {
      dispatch(uploadCoverPhotoFailed(error))
    })
}



// WEBPACK FOOTER //
// ./src/scripts/app/modules/profile/actions.js